/**
 * G5 learned tendencies, device side: derive the user's project-configuration defaults from every
 * checkout their machines report (the committed shared config inside each encrypted
 * `machine.projects` report, the machine's tendency facts, its dev root) and the sealed project
 * inventories. Each tendency carries the evidence behind it. The result is kept in the sealed
 * `setting` entity `tendencies` so every trusted device (and the native clients) sees the same.
 *
 * Only a strong tendency with a shared-config path is APPLIED to a new project's drafted config;
 * everything else is offered to the scoping agent and shown to the user as context.
 */
import { type ProjectConfig, parseProjectConfig, setConfigValues } from '@viko/project-config'
import {
  type MachineProjectsFrame,
  ProjectTendencies,
  SettingEntry,
  type SetupConfigKey,
  settingEntityId,
  type Tendency,
  type TendencyFacts,
} from '@viko/protocol'
import type { BrowserEntities } from './encrypted-entities.js'

export interface TendencySources {
  reports: readonly { machineId: string; report: MachineProjectsFrame }[]
  /** Sealed project inventories: technology names per project. */
  inventories?: readonly { projectId: string; technologies: readonly string[] }[]
  now?: number
}

interface ProjectView {
  key: string
  name: string
  config?: ProjectConfig
  facts?: TendencyFacts
  projectId?: string
}

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s)
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`

/** One view per project across machines (the same repo on two machines counts once). */
function projectsOf(reports: TendencySources['reports']): ProjectView[] {
  const byKey = new Map<string, ProjectView>()
  const get = (key: string, name: string) => {
    const hit = byKey.get(key)
    if (hit) return hit
    const v: ProjectView = { key, name }
    byKey.set(key, v)
    return v
  }
  for (const { report } of reports) {
    for (const p of report.projects) {
      if (!p.yaml) continue
      const parsed = parseProjectConfig(p.yaml)
      if (!parsed.ok) continue
      const v = get(parsed.config.project.id, parsed.config.project.name)
      v.config ??= parsed.config
    }
    for (const f of report.tendencyFacts ?? []) {
      const v = get(f.localId, f.name)
      v.facts ??= f
      if (f.projectId) v.projectId ??= f.projectId
    }
  }
  return [...byKey.values()]
}

/** Most common value with its supporters (ties: the first seen). */
function mode<T>(rows: readonly { v: T | undefined; name: string }[]) {
  const counts = new Map<string, { v: T; names: string[] }>()
  let of = 0
  for (const r of rows) {
    if (r.v === undefined) continue
    of++
    const k = JSON.stringify(r.v)
    const hit = counts.get(k)
    if (hit) hit.names.push(r.name)
    else counts.set(k, { v: r.v, names: [r.name] })
  }
  const best = [...counts.values()].sort((a, b) => b.names.length - a.names.length)[0]
  return best ? { ...best, support: best.names.length, of } : undefined
}

function strengthOf(support: number, of: number): Tendency['strength'] | undefined {
  if (support < 2) return undefined
  return support / of >= 0.6 ? 'strong' : 'weak'
}

/** First word pair of a command: `pnpm test --run` → `pnpm test`, `cd x && npm test` → `npm test`. */
export function recipeOf(cmd: string): string {
  const last = cmd.split(/&&|;/).pop()!.trim()
  return last.split(/\s+/).slice(0, 2).join(' ')
}

export function deriveTendencies(src: TendencySources): ProjectTendencies {
  const projects = projectsOf(src.reports)
  const items: Tendency[] = []
  const withConfig = projects.filter((p) => p.config)
  const withFacts = projects.filter((p) => p.facts)
  const push = (
    key: Tendency['key'],
    label: string,
    m: { v: unknown; names: string[]; support: number; of: number } | undefined,
    o: {
      value?: string
      apply?: SetupConfigKey
      explain: (value: string, support: number, of: number) => string
    },
  ) => {
    if (!m) return
    const strength = strengthOf(m.support, m.of)
    if (!strength) return
    const value = clip(o.value ?? String(m.v), 300)
    items.push({
      key,
      label,
      value,
      ...(o.apply && strength === 'strong' ? { apply: { path: o.apply, value: String(m.v) } } : {}),
      support: m.support,
      of: m.of,
      strength,
      explanation: clip(o.explain(value, m.support, m.of), 500),
      examples: m.names.slice(0, 8).map((n) => clip(n, 256)),
    })
  }
  const cfg = <T>(f: (c: ProjectConfig) => T | undefined) =>
    withConfig.map((p) => ({ v: f(p.config!), name: p.name }))
  const ofProjects = (n: number, of: number) => `${n} of ${plural(of, 'project')}`

  // Dev dir: where each machine keeps repos (learned locally from its checkouts).
  const roots = src.reports.flatMap((r) =>
    r.report.defaults?.devRoot
      ? [{ machine: r.report.machine, root: r.report.defaults.devRoot }]
      : [],
  )
  if (roots.length)
    items.push({
      key: 'devRoot',
      label: 'Dev folder',
      value: clip([...new Set(roots.map((r) => r.root))].join(' · '), 300),
      support: roots.length,
      of: src.reports.length,
      strength: 'strong',
      explanation: clip(
        `New checkouts go where your repos already live: ${roots.map((r) => `${r.machine} ${r.root}`).join(', ')}.`,
        500,
      ),
      examples: [],
    })

  push('targetBranch', 'Target branch', mode(cfg((c) => c.landing.targetBranch)), {
    apply: 'landing.targetBranch',
    explain: (v, n, of) => `${ofProjects(n, of)} land on ${v}.`,
  })
  push('loopProfile', 'Loop profile', mode(cfg((c) => c.loopProfile)), {
    apply: 'loopProfile',
    explain: (v, n, of) => `${ofProjects(n, of)} use the ${v} loop.`,
  })
  push('autonomy', 'Autonomy', mode(cfg((c) => c.autonomy.level)), {
    apply: 'autonomy.level',
    explain: (v, n, of) => `${ofProjects(n, of)} run at ${v} autonomy.`,
  })
  push('mergeMode', 'Merge mode', mode(cfg((c) => c.landing.mergeMode)), {
    apply: 'landing.mergeMode',
    explain: (v, n, of) => `${ofProjects(n, of)} land with ${v} merges.`,
  })
  push('defaultAgent', 'Default agent', mode(cfg((c) => c.routing.defaultAgent)), {
    apply: 'routing.defaultAgent',
    explain: (v, n, of) => `${ofProjects(n, of)} default to ${v}.`,
  })
  push('worktreeStrategy', 'Worktrees', mode(cfg((c) => c.worktree.strategy)), {
    apply: 'worktree.strategy',
    explain: (v, n, of) => `${ofProjects(n, of)} use the ${v} strategy.`,
  })
  // Recipes are offered, never applied: a brand-new repo has nothing to test yet.
  push(
    'testRecipe',
    'Test command',
    mode(cfg((c) => (c.landing.test[0] ? recipeOf(c.landing.test[0]) : undefined))),
    {
      explain: (v, n, of) => `${ofProjects(n, of)} gate landing on \`${v}\`.`,
    },
  )
  push(
    'buildRecipe',
    'Build command',
    mode(cfg((c) => (c.landing.build[0] ? recipeOf(c.landing.build[0]) : undefined))),
    {
      explain: (v, n, of) => `${ofProjects(n, of)} build with \`${v}\`.`,
    },
  )
  push(
    'packageManager',
    'Package manager',
    mode(withFacts.map((p) => ({ v: p.facts!.packageManager, name: p.name }))),
    { explain: (v, n, of) => `${ofProjects(n, of)} use ${v} (lockfiles).` },
  )

  // Release lanes: each target counted once per project.
  const targets = new Map<string, string[]>()
  for (const p of withConfig)
    for (const t of new Set(p.config!.releaseLanes.map((l) => l.target)))
      targets.set(t, [...(targets.get(t) ?? []), p.name])
  const lanes = [...targets.entries()]
    .filter(([, n]) => n.length >= 2)
    .sort((a, b) => b[1].length - a[1].length)
  if (lanes.length) {
    const top = lanes[0]!
    push(
      'releaseLanes',
      'Release lanes',
      {
        v: lanes.map(([t]) => t).join(', '),
        names: top[1],
        support: top[1].length,
        of: withConfig.length,
      },
      {
        explain: () =>
          `Lanes you ship through: ${lanes.map(([t, n]) => `${t} (${ofProjects(n.length, withConfig.length)})`).join(', ')}.`,
      },
    )
  }

  // Secrets: 1Password references (op://), never values.
  const op = withConfig.filter((p) => p.config!.secrets.refs.some((r) => r.ref.startsWith('op://')))
  if (op.length >= 2) {
    const personal = op.filter((p) =>
      p.config!.secrets.refs.some((r) => r.vault === 'personal'),
    ).length
    push(
      'secrets',
      'Secrets',
      { v: '1Password (op://)', names: op.map((p) => p.name), support: op.length, of: op.length },
      {
        explain: (_v, n) =>
          `${plural(n, 'project')} keep credentials as 1Password references${personal ? ` (${personal} also use your personal vault via Touch ID)` : ''}; agents use them through Viko without seeing values.`,
      },
    )
  }

  // Instruction files and style (from the machines' facts).
  const shapeLabel: Record<TendencyFacts['instructionShape'], string> = {
    pointer: 'CLAUDE.md imports AGENTS.md (one source)',
    duplicate: 'CLAUDE.md and AGENTS.md kept as copies',
    divergent: 'separate CLAUDE.md and AGENTS.md',
    single: 'a single instruction file',
    none: 'no instruction files',
  }
  const shape = mode(withFacts.map((p) => ({ v: p.facts!.instructionShape, name: p.name })))
  push('instructionFiles', 'Instruction files', shape, {
    ...(shape ? { value: shapeLabel[shape.v] } : {}),
    explain: (v, n, of) => `${ofProjects(n, of)} use ${v}.`,
  })
  const headingCounts = new Map<string, string[]>()
  for (const p of withFacts)
    for (const h of new Set(p.facts!.headings.map((x) => x.trim())))
      headingCounts.set(h, [...(headingCounts.get(h) ?? []), p.name])
  const shared = [...headingCounts.entries()]
    .filter(([, n]) => n.length >= 2)
    .sort((a, b) => b[1].length - a[1].length)
  if (shared.length)
    push(
      'instructionStyle',
      'Instruction style',
      {
        v: shared
          .slice(0, 6)
          .map(([h]) => h)
          .join(' · '),
        names: shared[0]![1],
        support: shared[0]![1].length,
        of: withFacts.length,
      },
      {
        explain: (v) => `Your instruction files share the sections ${v}.`,
      },
    )
  push(
    'taskDir',
    'Task files',
    mode(withFacts.map((p) => ({ v: p.facts!.taskDirs[0], name: p.name }))),
    {
      explain: (v, n, of) => `${ofProjects(n, of)} track tasks in ${v}.`,
    },
  )
  // Branch naming: prefixes used in at least two projects.
  const prefixes = new Map<string, string[]>()
  for (const p of withFacts)
    for (const b of p.facts!.branchPrefixes)
      prefixes.set(b.prefix, [...(prefixes.get(b.prefix) ?? []), p.name])
  const used = [...prefixes.entries()]
    .filter(([, n]) => n.length >= 2)
    .sort((a, b) => b[1].length - a[1].length)
  if (used.length)
    push(
      'branchPrefixes',
      'Branch naming',
      {
        v: used.map(([p]) => `${p}…`).join(', '),
        names: used[0]![1],
        support: used[0]![1].length,
        of: withFacts.length,
      },
      {
        explain: (v, n, of) => `${ofProjects(n, of)} name branches ${v}.`,
      },
    )
  // Stack: technologies in at least two projects' inventories.
  const tech = new Map<string, Set<string>>()
  for (const inv of src.inventories ?? [])
    for (const t of new Set(inv.technologies))
      tech.set(t, (tech.get(t) ?? new Set()).add(inv.projectId))
  const names = new Map(
    projects.flatMap((p) => (p.projectId ? [[p.projectId, p.name] as const] : [])),
  )
  const stack = [...tech.entries()]
    .filter(([, s]) => s.size >= 2)
    .sort((a, b) => b[1].size - a[1].size)
  if (stack.length) {
    const top = [...stack[0]![1]]
    push(
      'stack',
      'Stack',
      {
        v: stack
          .slice(0, 8)
          .map(([t]) => t)
          .join(', '),
        names: top.map((id) => names.get(id) ?? 'a project'),
        support: top.length,
        of: src.inventories!.length,
      },
      {
        explain: (v) => `Technologies across your inventories: ${v}.`,
      },
    )
  }

  return ProjectTendencies.parse({
    v: 1,
    derivedAt: src.now ?? Date.now(),
    projects: projects.length,
    machines: src.reports.length,
    items,
  })
}

/** Short explained lines for the scoping agent's brief (`project.scope` payload). */
export function tendencyLines(t: ProjectTendencies): string[] {
  return t.items.map((i) => clip(`${i.label}: ${i.value} — ${i.explanation}`, 300)).slice(0, 40)
}

export interface ConfigChoice {
  path: SetupConfigKey
  value: string
  source: 'learned' | 'agent'
  why: string
}

/**
 * Apply the strong, applicable tendencies (then the agent's `Config:` changes) to a drafted
 * config. An existing repo keeps what its audit found for the target branch.
 */
export function applyTendencies(
  yaml: string,
  t: ProjectTendencies | undefined,
  o: {
    existing?: boolean
    agent?: readonly { key: SetupConfigKey; value: string; why?: string }[]
  } = {},
): { yaml: string; choices: ConfigChoice[] } {
  const choices: ConfigChoice[] = []
  for (const i of t?.items ?? []) {
    if (!i.apply || i.strength !== 'strong') continue
    if (o.existing && i.apply.path === 'landing.targetBranch') continue
    choices.push({
      path: i.apply.path,
      value: i.apply.value,
      source: 'learned',
      why: i.explanation,
    })
  }
  for (const a of o.agent ?? []) {
    const rest = choices.filter(
      (c) => c.path !== a.key || a.key === 'landing.test' || a.key === 'landing.build',
    )
    choices.splice(0, choices.length, ...rest, {
      path: a.key,
      value: a.value,
      source: 'agent',
      why: a.why ?? 'Proposed in the scoping conversation.',
    })
  }
  let out = yaml
  const kept: ConfigChoice[] = []
  for (const c of choices) {
    try {
      out = setConfigValues(out, [{ path: c.path, value: c.value }])
      kept.push(c)
    } catch {
      /* a value this draft cannot take is dropped, never forced */
    }
  }
  return { yaml: out, choices: kept }
}

// ---- the sealed `tendencies` setting --------------------------------------------------------------

type Entities = Pick<BrowserEntities, 'list' | 'write'>

export async function loadTendencies(entities: Entities): Promise<ProjectTendencies | undefined> {
  const id = settingEntityId('tendencies')
  const row = (await entities.list('setting')).find((r) => r.id === id)
  const entry = SettingEntry.safeParse(row?.value)
  if (!entry.success || entry.data.name !== 'tendencies') return undefined
  const t = ProjectTendencies.safeParse(entry.data.value)
  return t.success ? t.data : undefined
}

const sameItems = (a: ProjectTendencies | undefined, b: ProjectTendencies) =>
  !!a &&
  JSON.stringify([a.items, a.projects, a.machines]) ===
    JSON.stringify([b.items, b.projects, b.machines])

/** Save when the derived tendencies changed (a no-op otherwise, so it is cheap to call). */
export async function saveTendencies(
  entities: Entities,
  t: ProjectTendencies,
  known?: ProjectTendencies,
): Promise<boolean> {
  if (sameItems(known ?? (await loadTendencies(entities)), t)) return false
  await entities.write(
    'setting',
    settingEntityId('tendencies'),
    SettingEntry.parse({
      name: 'tendencies',
      value: ProjectTendencies.parse(t),
      updatedAt: t.derivedAt,
    }),
  )
  return true
}
